import { FONT_REGISTRY, type FontRegistryEntry } from '@/config/fonts';

/**
 * Pure lookup of a brand font in the registry. `null` when the family is
 * absent/unknown (→ keep the default face). Split out from the DOM write so it
 * can be unit-tested in the node test environment.
 *
 * `fontFamily` is an untrusted DB string, and `FONT_REGISTRY` is an object
 * literal, so we must guard against inherited prototype keys ("constructor",
 * "toString", "__proto__"…) resolving to a truthy non-entry — the check on the
 * entry's own shape rejects those and returns `null`.
 */
export const resolveBrandFont = (fontFamily?: string): FontRegistryEntry | null => {
  const entry = fontFamily ? FONT_REGISTRY[fontFamily] : undefined;
  return entry && typeof entry.load === 'function' && typeof entry.stack === 'string' ? entry : null;
};

/**
 * Applies the brand font to the `--font-sans` CSS variable (the single knob the
 * app + custom sign-in read). Mirrors `applyPrimaryColorToDocument`.
 *
 * - Known family → lazily load its self-hosted webfont and point `--font-sans`
 *   at the brand stack.
 * - Unknown / no family → clear the inline override so the `:root` default
 *   (Inter) applies. This also prevents one partner's font leaking to another
 *   when the effective brand changes at runtime.
 */
export const applyBrandFontToDocument = (fontFamily?: string): void => {
  const entry = resolveBrandFont(fontFamily);

  if (entry) {
    // Fire-and-forget: a failed font import just keeps the default face.
    void entry.load().catch(() => undefined);
    document.documentElement.style.setProperty('--font-sans', entry.stack);
  } else {
    document.documentElement.style.removeProperty('--font-sans');
  }
};
